import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Briefcase, MapPin, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState, LoadingGrid, StatusBadge, inr } from "@/components/bits";
import api from "@/lib/api";

export default function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: "", location: "", work_mode: "", employment_type: "" });

  const load = async (f = filters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(f).forEach(([k, v]) => v && params.set(k, v));
      const { data } = await api.get(`/public/jobs?${params.toString()}`);
      setJobs(data.jobs);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="pt-16 min-h-screen" data-testid="jobs-page">
      <div className="navy-bg noise-overlay py-16">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <h1 className="text-3xl sm:text-4xl font-heading font-extrabold text-white">Find your next role</h1>
          <p className="text-white/60 mt-2">{total} open positions across India & international markets</p>
          <div className="grid sm:grid-cols-4 gap-3 mt-8 bg-white/5 border border-white/10 rounded-2xl p-4 backdrop-blur">
            <Input
              placeholder="Role, skill or company"
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              className="bg-white/10 border-white/15 text-white placeholder:text-white/40"
              data-testid="jobs-search-input"
            />
            <Input
              placeholder="Location"
              value={filters.location}
              onChange={(e) => setFilters({ ...filters, location: e.target.value })}
              className="bg-white/10 border-white/15 text-white placeholder:text-white/40"
              data-testid="jobs-location-input"
            />
            <Select value={filters.work_mode} onValueChange={(v) => setFilters({ ...filters, work_mode: v === "any" ? "" : v })}>
              <SelectTrigger className="bg-white/10 border-white/15 text-white" data-testid="jobs-workmode-select">
                <SelectValue placeholder="Work mode" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any mode</SelectItem>
                <SelectItem value="office">Office</SelectItem>
                <SelectItem value="remote">Remote</SelectItem>
                <SelectItem value="hybrid">Hybrid</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={() => load()} className="bg-[#F5A623] text-[#0B1B3D] hover:bg-[#f7b84d] font-semibold" data-testid="jobs-search-btn">
              <Search className="w-4 h-4 mr-2" /> Search
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {loading ? (
          <LoadingGrid count={6} />
        ) : jobs.length === 0 ? (
          <EmptyState title="No jobs match your search" description="Try broadening your filters." testId="jobs-empty" />
        ) : (
          <div className="grid gap-4" data-testid="jobs-list">
            {jobs.map((j) => (
              <Link key={j.id} to={`/jobs/${j.id}`} data-testid={`job-card-${j.id}`}>
                <div className="bg-white border border-border/60 rounded-xl p-6 card-hover flex flex-wrap items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-heading font-semibold text-lg text-[#0B1B3D]">{j.title}</h3>
                      <StatusBadge status={j.work_mode} />
                    </div>
                    <p className="text-sm text-muted-foreground mt-1.5 flex items-center gap-4 flex-wrap">
                      <span className="flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" /> {j.company_name}</span>
                      <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {j.location}</span>
                      <span>{j.experience_min}–{j.experience_max} yrs</span>
                    </p>
                    <div className="flex gap-2 mt-3 flex-wrap">
                      {(j.skills || []).slice(0, 4).map((s) => (
                        <span key={s} className="text-xs bg-[#2563EB]/8 text-[#2563EB] rounded-full px-2.5 py-1 font-medium">{s}</span>
                      ))}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-heading font-bold text-[#0B1B3D]">
                      {j.salary_min ? `${inr(j.salary_min)} – ${inr(j.salary_max)}` : "Salary undisclosed"}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">{j.vacancies} opening{j.vacancies > 1 ? "s" : ""}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
